import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import axios from 'axios'

/**
 * Dezabonarea de la mesajele comerciale, cu un clic din email.
 *
 * Victor, pe subsolul unui mesaj primit: „TE POȚI DEZABONA ORICÂND (...) trebuie să poată să fie
 * click-uibil și să ducă către o pagină care să îl scoată automat din baza de date". Până acum
 * textul trimitea la „linkul de dezabonare din fiecare mesaj comercial" — un link care nu exista.
 *
 * Pagina cere o confirmare, ca la Shopify, modelul pe care l-a arătat chiar el. Nu din pedanterie:
 * clienții de email și antivirusurile deschid preventiv linkurile din mesaje, ca să le verifice.
 * Dacă simpla deschidere ar dezabona, unii oameni s-ar trezi scoși din listă fără să fi apăsat
 * nimic — și n-am avea cum să aflăm vreodată.
 *
 * Spune și ce NU se întâmplă. „Să îl scoată din baza de date" sună a ștergere, dar sunt două
 * drepturi diferite: participarea la tombolă rămâne neatinsă (art. 3.5 din regulament), iar
 * procesele-verbale semnate se păstrează, fiind documente justificative. Cine voia doar să nu mai
 * primească reclame n-are de ce să iasă din extragere.
 */

interface Date_ {
  email: string | null
  prenume: string
  /** Nu mai are niciun acord activ — a apăsat deja, sau n-a bifat niciodată. */
  deja: boolean
}

export function DezabonarePage() {
  const { contactId = '', token = '' } = useParams()
  const [date, setDate] = useState<Date_ | null>(null)
  const [eroare, setEroare] = useState<string | null>(null)
  const [seTrimite, setSeTrimite] = useState(false)
  const [gata, setGata] = useState(false)

  const adresa = `/api/public/sampling/dezabonare/${encodeURIComponent(contactId)}/${encodeURIComponent(token)}`

  useEffect(() => {
    let anulat = false
    axios
      .get<Date_>(adresa)
      .then((r) => {
        if (!anulat) setDate(r.data)
      })
      .catch(() => {
        if (!anulat) {
          setEroare(
            'Linkul nu mai este valabil. Scrie-ne la gdpr@boost4life.ro și te scoatem din listă.',
          )
        }
      })
    return () => {
      anulat = true
    }
  }, [adresa])

  async function confirma() {
    setSeTrimite(true)
    try {
      await axios.post(adresa)
      setGata(true)
    } catch {
      setEroare('Nu am putut înregistra dezabonarea. Scrie-ne la gdpr@boost4life.ro.')
    } finally {
      setSeTrimite(false)
    }
  }

  return (
    <div className="min-h-screen bg-b4l-bg px-4 py-10">
      <div className="mx-auto max-w-md rounded-lg bg-white p-6">
        <h1 className="text-xl font-semibold text-b4l-text">Dezabonare</h1>

        {eroare && <p className="mt-4 text-sm text-b4l-red">{eroare}</p>}
        {!date && !eroare && <p className="mt-4 text-sm text-b4l-text-muted">Se încarcă…</p>}

        {date && !eroare && (gata || date.deja) ? (
          <>
            <p className="mt-4 text-sm text-b4l-text">
              {gata
                ? 'Gata — nu îți mai trimitem mesaje comerciale.'
                : 'Nu ești abonat(ă) la mesaje comerciale.'}
            </p>
            <Ramane />
          </>
        ) : (
          date &&
          !eroare && (
            <>
              <p className="mt-4 text-sm text-b4l-text">
                Confirmă că nu mai vrei să primești noutăți și oferte Boost4Life
                {date.email ? ' pe adresa' : ''}
              </p>
              {date.email && (
                <p className="mt-1 break-all font-medium text-b4l-text">{date.email}</p>
              )}

              <button
                onClick={() => void confirma()}
                disabled={seTrimite}
                className="mt-5 w-full rounded-lg bg-b4l-blue px-4 py-3 text-sm font-medium text-white disabled:opacity-60"
              >
                {seTrimite ? 'Se înregistrează…' : 'Confirmă dezabonarea'}
              </button>

              <Ramane />
            </>
          )
        )}
      </div>
    </div>
  )
}

/**
 * Ce rămâne neatins. Se arată în ambele stări, nu doar după confirmare: cine ezită trebuie să știe
 * ce pierde ÎNAINTE să apese, nu după.
 */
function Ramane() {
  return (
    <div className="mt-5 border-t border-b4l-border pt-4 text-xs leading-relaxed text-b4l-text-muted">
      <p>
        <strong className="text-b4l-text">Participarea la tombolă rămâne validă.</strong> Dacă ești
        înscris(ă), rămâi în extragere și te contactăm dacă ieși câștigător(oare) — dezabonarea
        oprește doar mesajele comerciale.
      </p>
      <p className="mt-2">
        Îți trimitem în continuare mesajele legate de înscriere: confirmarea, procesele-verbale
        semnate și anunțul de câștig. Fără ele nu ți-am putea preda premiul.
      </p>
      <p className="mt-2">
        Dacă vrei ștergerea completă a datelor tale, scrie-ne la{' '}
        <a href="mailto:gdpr@boost4life.ro" className="text-b4l-blue underline">
          gdpr@boost4life.ro
        </a>
        . Procesele-verbale semnate se păstrează și atunci, fiind documente justificative cu termen
        legal — dar datele din ele se înlocuiesc.
      </p>
    </div>
  )
}
